import { Command } from 'commander';
import { Table } from 'console-table-printer';
import pc from 'picocolors';
import { syncManager } from '../services/sync-manager';
import { webhookService } from '../services/webhook-service';
import { getOfflineQueue } from '../services/offline-queue';
import { logger } from '../utils/logger';

export function createSyncStatusCommand(): Command {
  const sync = new Command('sync-status')
    .alias('ss')
    .description('View and manage synchronization status')
    .option('-m, --metrics', 'Show sync metrics')
    .option('-q, --queue', 'Show offline queue status')
    .option('-f, --force', 'Force sync all pending operations')
    .option('-r, --reset', 'Reset sync metrics')
    .action(async (options) => {
      try {
        if (options.force) {
          // 强制同步所有待处理操作
          logger.info('Forcing sync of all pending operations...');
          await syncManager.forceSyncAll();
          logger.success('All pending operations synced');
          return;
        }

        if (options.reset) {
          // 重置同步指标
          syncManager.resetMetrics();
          logger.success('Sync metrics reset');
          return;
        }

        // 显示同步状态
        console.log(pc.bold('\n📊 Synchronization Status\n'));

        // 基本状态
        const webhookEnabled = process.env.WORKFLOW_WEBHOOK_ENABLED !== 'false';
        const webhookUrl = process.env.WORKFLOW_WEBHOOK_URL || 'http://localhost:3001/api/v1/webhook/sync';
        
        console.log(`${pc.dim('Webhook:')} ${webhookEnabled ? pc.green('Enabled') : pc.red('Disabled')}`);
        console.log(`${pc.dim('Webhook URL:')} ${webhookUrl}`);
        console.log();

        if (options.metrics) {
          // 显示同步指标
          const metrics = syncManager.getMetrics();
          
          const metricsTable = new Table({
            title: 'Sync Metrics',
            columns: [
              { name: 'metric', alignment: 'left', color: 'white' },
              { name: 'value', alignment: 'right', color: 'cyan' }
            ]
          });

          metricsTable.addRow({ 
            metric: 'Total Syncs', 
            value: metrics.totalSyncs.toString() 
          });
          metricsTable.addRow({ 
            metric: 'Successful', 
            value: metrics.successfulSyncs.toString() 
          });
          metricsTable.addRow({ 
            metric: 'Failed', 
            value: metrics.failedSyncs.toString() 
          });
          metricsTable.addRow({ 
            metric: 'Success Rate', 
            value: metrics.totalSyncs > 0 
              ? `${((metrics.successfulSyncs / metrics.totalSyncs) * 100).toFixed(1)}%`
              : 'N/A'
          });
          metricsTable.addRow({ 
            metric: 'Average Sync Time', 
            value: metrics.averageSyncTime > 0 
              ? `${metrics.averageSyncTime.toFixed(0)}ms`
              : 'N/A'
          });
          metricsTable.addRow({ 
            metric: 'Last Sync Time', 
            value: metrics.lastSyncTime > 0 
              ? `${metrics.lastSyncTime}ms`
              : 'N/A'
          });
          metricsTable.addRow({ 
            metric: 'Current Strategy', 
            value: metrics.syncStrategy 
          });

          metricsTable.printTable();
          console.log();
        }

        if (options.queue) {
          // 显示离线队列状态
          const offlineQueue = getOfflineQueue();
          const queueStatus = offlineQueue.getStatus();
          
          console.log(pc.bold('Offline Queue Status:'));
          console.log(`${pc.dim('Network:')} ${queueStatus.isOnline ? pc.green('Online') : pc.red('Offline')}`);
          console.log(`${pc.dim('Queue Length:')} ${queueStatus.queueLength}`);
          
          if (queueStatus.oldestOperation) {
            const ageMinutes = Math.floor(queueStatus.oldestOperation / 60000);
            console.log(`${pc.dim('Oldest Operation:')} ${ageMinutes} minutes ago`);
          }
          
          if (queueStatus.nextRetryIn) {
            const retrySeconds = Math.ceil(queueStatus.nextRetryIn / 1000);
            console.log(`${pc.dim('Next Retry In:')} ${retrySeconds} seconds`);
          }
          
          // Webhook 服务的离线队列状态
          const webhookQueueStatus = webhookService.getOfflineQueueStatus();
          if (webhookQueueStatus.queueLength > 0) {
            console.log();
            console.log(pc.bold('Webhook Queue:'));
            console.log(`${pc.dim('Pending Webhooks:')} ${webhookQueueStatus.queueLength}`);
          }
        }

        // 同步策略说明
        if (!options.metrics && !options.queue) {
          console.log(pc.bold('Available Sync Strategies:'));
          console.log();
          
          const strategies = [
            { name: 'Immediate', condition: 'User actions on single task', delay: '0ms' },
            { name: 'Fast', condition: 'Status/priority changes', delay: '1s' },
            { name: 'Delayed', condition: 'Content edits (≤5 tasks)', delay: '3s' },
            { name: 'Batch', condition: 'Multiple tasks (>5)', delay: '5s' },
            { name: 'Offline-First', condition: 'Network issues', delay: 'Queue' },
            { name: 'Resource-Saving', condition: 'High CPU/Memory usage', delay: '10s' }
          ];

          const strategyTable = new Table({
            columns: [
              { name: 'strategy', alignment: 'left', color: 'white' },
              { name: 'condition', alignment: 'left', color: 'cyan' },
              { name: 'delay', alignment: 'right', color: 'yellow' }
            ]
          });

          strategies.forEach(s => strategyTable.addRow(s));
          strategyTable.printTable();
        }

      } catch (error) {
        logger.error('Failed to get sync status:', error);
        process.exit(1);
      }
    });

  sync
    .command('enable')
    .description('Enable webhook synchronization')
    .action(() => {
      process.env.WORKFLOW_WEBHOOK_ENABLED = 'true';
      webhookService.setEnabled(true);
      logger.success('Webhook synchronization enabled');
    });

  sync
    .command('disable')
    .description('Disable webhook synchronization')
    .action(() => {
      process.env.WORKFLOW_WEBHOOK_ENABLED = 'false';
      webhookService.setEnabled(false);
      logger.success('Webhook synchronization disabled');
    });

  sync
    .command('set-url <url>')
    .description('Set custom webhook URL')
    .action((url) => {
      process.env.WORKFLOW_WEBHOOK_URL = url;
      webhookService.setWebhookUrl(url);
      logger.success(`Webhook URL set to: ${url}`);
    });

  sync
    .command('test')
    .description('Test webhook connection')
    .action(async () => {
      try {
        logger.info('Testing webhook connection...');
        
        // 发送测试事件
        await webhookService.notify('test', {
          message: 'Test webhook from CLI',
          timestamp: new Date().toISOString()
        });
        
        logger.success('Webhook test sent successfully');
      } catch (error) {
        logger.error('Webhook test failed:', error);
      }
    });

  return sync;
}